import { Collection, fromAppwrite, PlasticsMoldingTask, runAppwriteTransaction } from '@prism/prism-shared';
import type { TablesDB } from 'appwrite';

const editableFields = ['totalQuantity', 'completedQuantity', 'note', 'orderedProductId'] as const;

/** Administrative dialog edits only write changed fields on the task row. */
export async function saveMoldingTaskAdminEdit(
    tables: Pick<TablesDB, 'getRow' | 'updateRow' | 'createTransaction' | 'updateTransaction'>,
    databaseId: string,
    original: PlasticsMoldingTask,
    edited: PlasticsMoldingTask,
): Promise<void> {
    if (!original?.id || original.id !== edited.id) throw new Error('Choose a valid molding task.');
    const changedFields = editableFields.filter(field => edited[field] !== original[field]);
    if (!changedFields.length) return;
    for (const field of changedFields) {
        const value = edited[field];
        if (field === 'totalQuantity' || field === 'completedQuantity') {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) throw new Error('Task quantities must be nonnegative numbers.');
        } else if (typeof value !== 'string') throw new Error('Provide valid task notes and order links.');
    }

    await runAppwriteTransaction(tables, async transactionId => {
        const taskRow = await tables.getRow({ databaseId, tableId: Collection.plasticsMoldingTask, rowId: original.id });
        const current = fromAppwrite<PlasticsMoldingTask>(Collection.plasticsMoldingTask, taskRow);
        for (const field of changedFields) {
            // A retry after a lost commit response is safe when the requested value is already saved.
            if (current[field] !== original[field] && current[field] !== edited[field]) {
                throw new Error('This task changed while the dialog was open. Reopen it before saving.');
            }
        }
        await tables.updateRow({ databaseId, tableId: Collection.plasticsMoldingTask, rowId: original.id, transactionId,
            data: Object.fromEntries(changedFields.map(field => [field, edited[field]])) });
        // Cover a change between the fresh read and staging. Appwrite guards later changes at commit.
        const fresh = await tables.getRow({ databaseId, tableId: Collection.plasticsMoldingTask, rowId: taskRow.$id });
        if (fresh.$updatedAt !== taskRow.$updatedAt) throw new Error('The task changed during saving. Refresh and try again.');
    });
}
